/**
 * ALF実績の集計期間と、一覧全体の要約を決める。
 *
 * 期間に汎用の `date-range-presets.ts` は使わない。あちらは「今日まで」を返すが、
 * ALF実績の当日ぶんはまだオペレーターが介入しうる途中経過で、完結率が実際より
 * 低く出る。低い完結率は「直すべき記事」の判断に直結するため、締まった日
 * （前日）までしか見せない。選択肢も 7 日と 28 日に絞る（記事単位まで割ると
 * 母数が小さく、週次未満では完結率が偶然で振れる）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business ALF実績の集計期間（前日までの直近N日）・引用回数の下限・一覧全体の要約を決める
 * @graph-connects none
 */

import type { FaqAlfPerformanceItem, FaqAlfPerformanceResponse } from '../types/faq-monitor.js';

/** @graph-connects none */
export const ALF_PERIOD_PRESET_DAYS = [7, 28] as const;

/** @graph-connects none */
export type AlfPeriodPreset = (typeof ALF_PERIOD_PRESET_DAYS)[number] | 'custom';

/**
 * 既定の期間。API 側の既定（`ALF_DEFAULT_PERIOD_DAYS`）と揃えている。
 * @graph-connects none
 */
export const ALF_DEFAULT_PERIOD_DAYS = 28;

/**
 * 引用回数の下限の選択肢。母数が小さいと完結率が偶然で振れるため既定は 4。
 * @graph-connects none
 */
export const ALF_MIN_CITED_OPTIONS = [3, 4, 10] as const;

/** @graph-connects none */
export const ALF_DEFAULT_MIN_CITED = 4;

/**
 * 日本時間の今日を `YYYY-MM-DD` で返す。
 *
 * 画面を開く端末の時計に依らせない。海外から開いても、BQ 側（JST 基準）と
 * 同じ日付で期間を組み立てる。
 * @graph-connects none
 */
export function todayJst(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * `YYYY-MM-DD` を日数ぶんずらす。UTC で扱うので端末のタイムゾーンに影響されない。
 * @graph-connects none
 */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * 前日を終端とする直近 N 日の期間を返す。
 * @graph-connects todayJst [calls] 日本時間の今日
 * @graph-connects shiftDate [calls] 日付のずらし
 */
export function computeAlfPeriod(
  days: number,
  now: Date = new Date(),
): { from: string; to: string } {
  const to = shiftDate(todayJst(now), -1);
  return { from: shiftDate(to, -(days - 1)), to };
}

/**
 * 選択期間が、記録の残っていない日を含むか。
 *
 * 含む場合、その日より前は「ALF が動かなかった」のではなく「記録が無い」だけで、
 * 遡るほど完結率が下がって見える。画面はこれを注記で明示する。
 * @graph-connects none
 */
export function includesUnrecordedDays(
  periodFrom: string,
  dataAvailableFrom: string | null,
): boolean {
  if (dataAvailableFrom === null) return false;
  return periodFrom < dataAvailableFrom;
}

/**
 * 「見直したい記事」に数える完結率の上限（%）。半分も自力で終われていない記事。
 * @graph-connects none
 */
export const NEEDS_REVIEW_RATE_THRESHOLD = 50;

/**
 * 完結率の帯。低いほど直す価値が高い可能性がある（確定ではない。中身を読む前提）。
 * @graph-connects none
 */
export type AlfRateTone = 'low' | 'mid' | 'high';

/** @graph-connects NEEDS_REVIEW_RATE_THRESHOLD [references] 低い帯の境界 */
export function alfRateTone(rate: number): AlfRateTone {
  if (rate < NEEDS_REVIEW_RATE_THRESHOLD) return 'low';
  if (rate < 75) return 'mid';
  return 'high';
}

/**
 * 「人が対応すべき案件」として一覧から外した記事か。
 *
 * 完結率が低い記事には「FAQ の中身が足りない」ものと「そもそも人が対応すべき」
 * ものが混ざる。後者を `wont_fix` に倒すと、見直したい記事の数から外れる。
 * @graph-connects none
 */
export function isOutOfScope(item: FaqAlfPerformanceItem): boolean {
  return item.disposition.status === 'wont_fix';
}

/** @graph-connects none */
export interface AlfOverview {
  /** 一覧に出ている記事数（引用が下限以上、かつ FAQ 記事と結び付いたもの） */
  listedTitles: number;
  /** 引用が FAQ 記事と結び付いた記事の総数（下限未満を含む） */
  matchedTitles: number;
  /** 一覧に出ている記事ぶんの引用問い合わせ数 */
  listedCitedChats: number;
  /** 期間内の引用問い合わせ数の総数（下限未満・記事未特定を含む） */
  totalCitedChats: number;
  listedResolvedChats: number;
  /** 一覧ぶんの完結率（%）。引用が0なら null */
  listedResolveRate: number | null;
  /** 見直したい記事の数。完結率が低く、かつ人が対応すべき案件に倒していないもの */
  needsReview: number;
}

/**
 * 一覧全体の要約。
 *
 * 完結率は「一覧に出ている記事ぶん」でしか出せない。下限未満の記事と記事未特定の
 * 引用について、API は問い合わせ数しか返さず完結数を返さないため。母数を混ぜて
 * 割ると実態より低く出るので、分母は一覧ぶんに閉じ、総数は別の数字として並べる。
 * @graph-connects isOutOfScope [calls] 人が対応すべき案件かの判定
 * @graph-connects NEEDS_REVIEW_RATE_THRESHOLD [references] 見直したい記事の境界
 */
export function summarizeAlfPerformance(data: FaqAlfPerformanceResponse): AlfOverview {
  const listedCitedChats = data.items.reduce((sum, item) => sum + item.citedChats, 0);
  const listedResolvedChats = data.items.reduce((sum, item) => sum + item.alfResolvedChats, 0);

  return {
    listedTitles: data.items.length,
    matchedTitles: data.items.length + data.belowThresholdTitles,
    listedCitedChats,
    totalCitedChats: listedCitedChats + data.belowThresholdCitedChats + data.unmatchedCitedChats,
    listedResolvedChats,
    listedResolveRate:
      listedCitedChats === 0
        ? null
        : Math.round((listedResolvedChats / listedCitedChats) * 1000) / 10,
    needsReview: data.items.filter(
      (item) => item.alfResolveRate < NEEDS_REVIEW_RATE_THRESHOLD && !isOutOfScope(item),
    ).length,
  };
}

/**
 * Channel Talk の公式統計画面へのリンク。画面と同じ期間を渡す。
 *
 * こちらの完結率は公式値より 5〜9 ポイント低く出る（スタッフが社内向けに残した
 * メモを「スタッフが対応した」と数えてしまうため）。数字が食い違ったときに
 * 突き合わせられるよう、同じ期間の公式画面をいつでも開けるようにしておく。
 * @graph-connects none
 */
export function channelTalkStatisticsUrl(from: string, to: string): string {
  const params = new URLSearchParams({ from, to, type: 'chat' });
  return `https://channel.works/aircloset-mens/alf-customer/statistics?${params.toString()}`;
}
